import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import QuickAddMenu from '../../components/ui/QuickAddMenu';
import SEO from '../../components/SEO';
import Button from '../../components/ui/Button';
import ContractorFilters from './components/ContractorFilters';
import ContractorTable from './components/ContractorTable';
import ContractorMobileCard from './components/ContractorMobileCard';
import ContractorProfile from './components/ContractorProfile';
import AddContractorModal from './components/AddContractorModal';

import PermissionDenied from '../../components/ui/PermissionDenied';
import SkeletonRow from '../../components/ui/SkeletonRow';
import SkeletonCard from '../../components/ui/SkeletonCard';
import EmptyState from '../../components/ui/EmptyState';
import { usePermissions } from '../../hooks/usePermissions';
import { contractorService } from '../../services/contractorService';
import { supabase } from '../../lib/supabase';
import { exportToCSV, CONTRACTOR_CSV_COLUMNS } from '../../utils/exportToCSV';

const Contractors = () => {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissionError, setPermissionError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadContractors();

    // Listen for openContractorProfile event from other components
    const handleOpenContractorProfile = async (event) => {
      const { contractorId } = event?.detail || {};
      if (contractorId) {
        try {
          const contractor = await contractorService?.getById(contractorId);
          if (contractor) {
            setSelectedContractor(contractor);
          }
        } catch (error) {
          console.error('Error loading contractor:', error);
        }
      }
    };

    window.addEventListener('openContractorProfile', handleOpenContractorProfile);

    return () => {
      window.removeEventListener('openContractorProfile', handleOpenContractorProfile);
    };
  }, []);

  // Real-time subscription for contractors
  useEffect(() => {
    const channel = supabase?.channel('contractors_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contractors'
        },
        (payload) => {
          console.log('Contractor change detected:', payload);
          // Reload contractors when any change occurs
          loadContractors();
        }
      )?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contractor_availability'
        },
        (payload) => {
          console.log('Contractor availability change detected:', payload);
          // Reload contractors when availability changes
          loadContractors();
        }
      )?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_assignments'
        },
        (payload) => {
          console.log('Contractor booking change detected:', payload);
          // Reload contractors when project assignments change
          loadContractors();
        }
      )?.subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase?.removeChannel(channel);
    };
  }, []);

  const loadContractors = async () => {
    setLoading(true);
    try {
      const data = await contractorService?.getAll();
      setContractors(data || []);
    } catch (error) {
      console.error('Error loading contractors:', error);
      setContractors([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedContractors = useMemo(() => {
    let filtered = contractors?.filter(contractor => {
      const matchesSearch = !searchQuery || 
        contractor?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        contractor?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        contractor?.phone?.includes(searchQuery);
      
      const matchesSpecialty = specialtyFilter === 'all' || contractor?.role === specialtyFilter;
      const matchesAvailability = availabilityFilter === 'all' || contractor?.availabilityStatus === availabilityFilter;
      
      let matchesRating = true;
      if (ratingFilter !== 'all') {
        const rating = contractor?.rating || 0;
        if (ratingFilter === '4+') matchesRating = rating >= 4;
        else if (ratingFilter === '3+') matchesRating = rating >= 3 && rating < 4;
        else if (ratingFilter === '<3') matchesRating = rating < 3;
      }
      
      return matchesSearch && matchesSpecialty && matchesAvailability && matchesRating;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];
      
      if (sortField === 'name') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [contractors, searchQuery, specialtyFilter, availabilityFilter, ratingFilter, sortField, sortDirection]);

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleContractorClick = (contractor) => {
    setSelectedContractor(contractor);
  };

  const handleCloseProfile = () => {
    setSelectedContractor(null);
    loadContractors();
  };

  const handleContractorUpdate = () => {
    loadContractors();
  };

  const handleAddContractor = () => {
    if (!permissions?.canCreate) {
      setPermissionError('You do not have permission to add contractors.');
      setTimeout(() => setPermissionError(''), 4000);
      return;
    }
    setShowAddModal(true);
  };

  const handleExportCSV = () => {
    const exportData = filteredAndSortedContractors?.map(c => ({
      ...c,
      rating: c?.rating ? `${c?.rating}/5` : 'N/A',
      activeAssignments: c?.activeProjects ?? 0
    }));
    exportToCSV(
      exportData,
      CONTRACTOR_CSV_COLUMNS,
      `contractors_export_${new Date()?.toISOString()?.slice(0, 10)}`
    );
  };

  return (
    <>
      <SEO
        title="Contractor Management — ADU Subcontractor Directory"
        description="Manage your ADU construction contractors and subcontractors. Track specialties, availability, performance ratings, and project assignments with ADU Flow."
        canonical="/contractors"
        keywords="ADU contractors, subcontractors, contractor management, construction directory"
        noIndex={true}
      />
      <Header />
      <QuickAddMenu />
      <div className="main-content">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">Contractors</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">
                Manage contractor profiles, availability, and project assignments
              </p>
            </div>
            <div className="flex items-center gap-3">
              {permissions?.canCreate && (
                <Button
                  variant="default"
                  size="default"
                  iconName="UserPlus"
                  iconPosition="left"
                  onClick={handleAddContractor}
                >
                  Add Contractor
                </Button>
              )}
              <Button
                variant="outline"
                size="default"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportCSV}
                disabled={!filteredAndSortedContractors?.length}>
                Export CSV
              </Button>
            </div>
          </div>

          {/* Permission Denied Banner */}
          {permissionError && (
            <div className="mb-4">
              <PermissionDenied message={permissionError} />
            </div>
          )}

          {/* Filters */}
          <ContractorFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            specialtyFilter={specialtyFilter}
            onSpecialtyChange={setSpecialtyFilter}
            availabilityFilter={availabilityFilter}
            onAvailabilityChange={setAvailabilityFilter}
            ratingFilter={ratingFilter}
            onRatingChange={setRatingFilter}
            resultCount={filteredAndSortedContractors?.length}
          />

          {/* Desktop Table View */}
          <div className="hidden md:block">
            {loading ? (
              <SkeletonRow variant="contractor" count={5} />
            ) : filteredAndSortedContractors?.length === 0 ? (
              <div className="bg-card rounded-xl border border-border">
                <EmptyState
                  variant={searchQuery || specialtyFilter !== 'all' || availabilityFilter !== 'all' || ratingFilter !== 'all' ? 'contractors-filtered' : 'contractors'}
                  canCreate={permissions?.canCreate}
                  onAction={permissions?.canCreate ? handleAddContractor : undefined}
                  actionLabel="Add Contractor"
                />
              </div>
            ) : (
              <ContractorTable
                contractors={filteredAndSortedContractors}
                onContractorClick={handleContractorClick}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            )}
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {loading ? (
              <SkeletonCard variant="contractor-mobile" count={4} />
            ) : filteredAndSortedContractors?.length === 0 ? (
              <div className="bg-card rounded-xl border border-border">
                <EmptyState
                  variant={searchQuery || specialtyFilter !== 'all' || availabilityFilter !== 'all' || ratingFilter !== 'all' ? 'contractors-filtered' : 'contractors'}
                  canCreate={permissions?.canCreate}
                  onAction={permissions?.canCreate ? handleAddContractor : undefined}
                  actionLabel="Add Contractor"
                />
              </div>
            ) : (
              filteredAndSortedContractors?.map((contractor) => (
                <ContractorMobileCard
                  key={contractor?.id}
                  contractor={contractor}
                  onClick={() => handleContractorClick(contractor)}
                />
              ))
            )}
          </div>
        </div>
      </div>
      {/* Contractor Profile Modal */}
      {selectedContractor && (
        <ContractorProfile
          contractor={selectedContractor}
          onClose={handleCloseProfile}
          onUpdate={handleContractorUpdate}
        />
      )}
      {/* Add Contractor Modal */}
      {showAddModal && (
        <AddContractorModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadContractors();
          }}
        />
      )}
    </>
  );
};

export default Contractors;